import classNames from 'classnames';
import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import Popup from 'reactjs-popup';
import { ReactComponent as CloseIcon } from '../../assets/images/close-button.svg';
import styles from './HardwareFirstQuiz.module.scss';
import CalculatingStep from './Steps/CalculatingStep';
import ChipStep from './Steps/ChipStep';
import EmailStep from './Steps/EmailStep';
import RadioStep from './Steps/RadioStep';
import ResultStep from './Steps/ResultStep';
import { ChipStep as ChipStepDef, PROGRESS_STEP_COUNT, RadioStep as RadioStepDef } from './steps';
import captureQuizCompletion from './quizCapture';
import { trackEmailSkipped, trackStepAnswered, trackStepEntered } from './quizAnalytics';
import useQuizFlow from './useQuizFlow';

interface HardwareFirstQuizProps {
  open: boolean;
  onClose(): void;
  /** Acts on the recommendation. Wired to the shop's device selection in 5/5. */
  onContinue(deviceKey: 'mini' | 's3'): void;
}

/**
 * The inline product-recommendation quiz on the hardware-first shop.
 *
 * Drives `reactjs-popup` directly rather than going through
 * `SimpleDismissableModal`, which requires a `trigger` element: this sheet is
 * opened programmatically from every quiz CTA on the site, so it has no trigger
 * of its own.
 */
export default function HardwareFirstQuiz({ open, onClose, onContinue }: HardwareFirstQuizProps) {
  const { t } = useTranslation();
  const flow = useQuizFlow();
  const closeButtonRef = useRef<HTMLButtonElement | null>(null);

  // Ref callback rather than a plain ref: lazy content remounts the popup's
  // dialog element and wipes attributes set only in onOpen (see
  // SimpleDismissableModal, which has the same hack).
  const attachCloseButton = (node: HTMLButtonElement | null) => {
    closeButtonRef.current = node;
    node?.closest('[role="dialog"]')?.setAttribute('aria-modal', 'true');
  };

  // One event per question view, and one per completion. Keyed on the step id
  // rather than the index so a reordered flow keeps reporting the same events.
  const { phase, step, answers, recommendation } = flow;
  useEffect(() => {
    if (open && phase === 'question') {
      trackStepEntered(step.id);
    }
  }, [open, phase, step.id]);

  useEffect(() => {
    if (phase === 'result' && recommendation) {
      captureQuizCompletion(answers, recommendation);
    }
    // Capture runs once per completed quiz; `answers` is settled by this point.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase, recommendation]);

  const close = () => {
    onClose();
    flow.restart();
  };

  return (
    <Popup
      className="s3-popup"
      open={open}
      onOpen={() => closeButtonRef.current?.focus()}
      onClose={close}
      modal
      closeOnDocumentClick
    >
      <div className={styles.sheet}>
        <span className={styles.grabber} />
        <div className={styles.header}>
          {flow.canGoBack && (
            <button type="button" className={styles.back} aria-label={t('hf_quiz_back')} onClick={flow.back}>
              <span className={styles.backChevron} />
            </button>
          )}
          {flow.phase === 'result' && (
            <button type="button" className={styles.restart} onClick={flow.restart}>
              {t('hf_quiz_restart')}
            </button>
          )}
          {flow.phase === 'question' && (
            <div
              className={styles.progress}
              role="progressbar"
              aria-valuemin={1}
              aria-valuemax={PROGRESS_STEP_COUNT}
              aria-valuenow={flow.stepIndex + 1}
              aria-label={t('hf_quiz_progress', { current: flow.stepIndex + 1, total: PROGRESS_STEP_COUNT })}
            >
              {Array.from({ length: PROGRESS_STEP_COUNT }, (_, i) => (
                <span
                  key={i}
                  className={classNames(styles.progressSegment, {
                    [styles.progressSegmentFilled]: i <= flow.stepIndex,
                  })}
                />
              ))}
            </div>
          )}
          <button
            type="button"
            ref={attachCloseButton}
            className={styles.close}
            aria-label={t('hf_quiz_close')}
            onClick={close}
          >
            <CloseIcon />
          </button>
        </div>
        {flow.phase === 'calculating' && <CalculatingStep answers={flow.answers} onDone={flow.showResult} />}
        {flow.phase === 'result' && flow.recommendation && (
          <ResultStep recommendation={flow.recommendation} onContinue={onContinue} />
        )}
        {flow.phase === 'question' && flow.step.kind === 'radio' && (
          <RadioStep
            step={flow.step as RadioStepDef<string>}
            selected={flow.answers[flow.step.id as 'age' | 'size' | 'residence']}
            onSelect={(optionId) => {
              trackStepAnswered(flow.step.id, optionId);
              flow.selectRadio(flow.step as RadioStepDef<string>, optionId);
            }}
          />
        )}
        {flow.phase === 'question' && flow.step.kind === 'chip' && (
          <ChipStep
            step={flow.step as ChipStepDef<string>}
            selected={flow.answers[flow.step.id as 'exercise' | 'importance']}
            onToggle={(optionId) => flow.toggleChip(flow.step as ChipStepDef<string>, optionId)}
            onNext={() => {
              trackStepAnswered(flow.step.id, flow.answers[flow.step.id as 'exercise' | 'importance']);
              flow.next();
            }}
            canAdvance={flow.canAdvance}
          />
        )}
        {flow.phase === 'question' && flow.step.kind === 'email' && (
          <EmailStep
            titleKey={flow.step.titleKey}
            value={flow.answers.email}
            onChange={flow.setEmail}
            onSubmit={() => {
              trackStepAnswered('email', flow.answers.email);
              flow.next();
            }}
            onSkip={() => {
              trackEmailSkipped();
              flow.setEmail(null);
              flow.next();
            }}
          />
        )}
      </div>
    </Popup>
  );
}
